import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import useUnsavedWarning from '../../../hooks/useUnsavedWarning';
import Button from "../../../components/Button";
import TextInput from "../../../components/TextInput";
import Upload from "../../../components/Upload";
import { Trash2, Plus } from "lucide-react";
import { getWhyChooseUs, saveWhyChooseUs, apiUrl } from "../../../api";
import PageLoader from "../../../components/PageLoader";
import Swal from "sweetalert2";

const AdminCMSWhyChooseUs = () => {
  const initialValues = {
    title: "Why Market Mavericks?",
    image: "",
    points: [],
  };

  const [form, setForm] = useState(initialValues);
  const [originalForm, setOriginalForm] = useState(initialValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});

  const isFormInvalid = useMemo(() => {
    if (!form.title?.trim()) return true;
    if (!form.image) return true;
    if (form.points.length === 0) return true;
    return form.points.some((p) => !p.title?.trim() || !p.description?.trim());
  }, [form]);

  const isChanged = useMemo(() => {
    return JSON.stringify(form) !== JSON.stringify(originalForm);
  }, [form, originalForm]);
  useUnsavedWarning(isChanged);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const stored = await getWhyChooseUs();
        if (!mounted) return;

        if (stored && typeof stored === "object") {
          const data = {
            title: stored.title || "",
            image: stored.image || "",
            points: Array.isArray(stored.points)
              ? stored.points.map((p) => ({
                  title: p.title || "",
                  description: p.description || "",
                }))
              : [],
          };
          setForm(data);
          setOriginalForm(JSON.parse(JSON.stringify(data)));
        }
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            err.response?.data?.message ||
            err.message ||
            "Failed to load content",
          confirmButtonColor: "#38915c",
        });
        setOriginalForm(initialValues);
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const updateField = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setTouched((prev) => ({ ...prev, [field]: true }));
    if (!value?.trim() && field !== "image") {
      setErrors((prev) => ({ ...prev, [field]: "This field is required" }));
    } else {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const updatePoint = (index, field, value) => {
    setForm((f) => {
      const list = [...(f.points || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...f, points: list };
    });
  };

  const addPoint = () => {
    setForm((f) => ({
      ...f,
      points: [...(f.points || []), { title: "", description: "" }],
    }));
    Swal.fire({
      icon: "success",
      title: "Point Added",
      text: "New feature point added to editor.",
      timer: 1500,
      showConfirmButton: false,
    });
  };

  const removePoint = async (index) => {
    const confirmed = await Swal.fire({
      title: "Remove point?",
      text: "This feature point will be removed from the editor.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove it!",
    });

    if (confirmed.isConfirmed) {
      setForm((f) => ({
        ...f,
        points: (f.points || []).filter((_, i) => i !== index),
      }));
      Swal.fire({
        icon: "success",
        title: "Removed",
        text: "Point removed. Remember to save changes.",
        timer: 1500,
        showConfirmButton: false,
      });
    }
  };

  const handleSave = async () => {
    if (isFormInvalid) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Fields",
        text: "Please fill in all required fields and upload an image.",
        confirmButtonColor: "#38915c",
      });
      return;
    }

    setSaving(true);
    try {
      const saved = await saveWhyChooseUs(form);
      if (saved && typeof saved === "object") {
        const data = {
          title: saved.title || "",
          image: saved.image || "",
          points: Array.isArray(saved.points) ? saved.points : [],
        };
        setForm(data);
        setOriginalForm(JSON.parse(JSON.stringify(data)));
        setErrors({});
        setTouched({});
        Swal.fire({
          icon: "success",
          title: "Saved",
          text: "Why Choose Us content updated successfully",
          confirmButtonColor: "#38915c",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err.response?.data?.message ||
          err.message ||
          "Failed to save content",
        confirmButtonColor: "#38915c",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageLoader />;
  }



  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <Link
            to="/admin/cms"
            className="text-sm text-primary-600 hover:underline mb-1 inline-block"
          >
            ← CMS Pages
          </Link>
          <h1 className="text-2xl font-bold text-gray-800">Why Choose Us</h1>
          <p className="text-gray-600 text-sm mt-1">
            Manage the "Why Market Mavericks?" section on the landing page.
          </p>
        </div>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={saving || !isChanged}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="space-y-6">
        {/* Main Title */}
        <section className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Main Content
          </h2>
          <TextInput
            label="Section Title"
            placeholder="e.g. Why Market Mavericks?"
            value={form.title}
            onChange={(e) => updateField("title", e.target.value)}
            error={touched.title && errors.title}
            required
          />
        </section>

        {/* Owner Image */}
        <section className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Owner / Section Image
          </h2>
          <Upload
            label="Featured Image"
            value={form.image}
            uploadEndpoint={apiUrl("/api/uploads/image")}
            onChange={(val) => updateField("image", val)}
            placeholder="Paste image URL or upload file"
          />
          <p className="mt-2 text-xs text-gray-500">
            This image is displayed on the landing page next to the features.
          </p>
        </section>

        {/* Features Points */}
        <section className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">
              Feature Points
            </h2>
            <button
              onClick={addPoint}
              className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              <Plus size={16} /> Add Point
            </button>
          </div>

          {form.points.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-gray-100 rounded-xl">
              <p className="text-sm text-gray-400">
                No feature points added yet.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {form.points.map((point, i) => (
                <div
                  key={i}
                  className="group relative p-4 bg-gray-50 rounded-xl border border-gray-100 transition-all hover:bg-white hover:shadow-md hover:border-primary-200"
                >
                  <button
                    onClick={() => removePoint(i)}
                    className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-danger-600 transition-colors"
                    title="Remove point"
                  >
                    <Trash2 size={16} />
                  </button>
                  <div className="grid gap-4">
                    <TextInput
                      label={`Point #${i + 1} Title`}
                      placeholder="e.g. 100% Practical"
                      value={point.title}
                      onChange={(e) => updatePoint(i, "title", e.target.value)}
                      error={!point.title?.trim() && "Required"}
                      required
                    />
                    <TextInput
                      label="Description"
                      placeholder="Explain this feature..."
                      value={point.description}
                      onChange={(e) =>
                        updatePoint(i, "description", e.target.value)
                      }
                      error={!point.description?.trim() && "Required"}
                      multiline
                      rows={2}
                      required
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminCMSWhyChooseUs;
